import { get, post } from './api-router.js';
import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';

/**
 * Utilitaire pour gérer les informations et opérations sur les caches KV
 * Utilise l'API du worker wab-config pour récupérer les données
 */

/**
 * Configuration de l'API pour les caches KV
 */
const KV_CACHE_CONFIG = {
  baseRoute: '/api/config/tool/kv-cache',
  endpoints: {
    info: '',
    clear: '/clear',
    namespace: '/namespace'
  }
};

/**
 * Récupère les informations globales sur les caches KV
 * @returns {Promise<Object>} Informations sur les caches KV
 */
export async function getKVCacheInfo() {
  try {
    debug.log(config, 'Récupération des informations sur les caches KV...');
    
    const response = await get(KV_CACHE_CONFIG.baseRoute + KV_CACHE_CONFIG.endpoints.info);
    
    if (!response.success) {
      throw new Error(response.error || 'Erreur lors de la récupération des informations KV');
    }
    
    debug.log(config, 'Informations sur les caches KV récupérées:', response.data);
    return response.data;
    
  } catch (error) {
    debug.error(config, 'Erreur lors de la récupération des informations KV:', error);
    throw error;
  }
}

/**
 * Récupère les détails d'un namespace spécifique
 * @param {string} namespace - Nom du namespace
 * @returns {Promise<Object>} Détails du namespace
 */
export async function getKVCacheNamespace(namespace) {
  try {
    debug.log(config, `Récupération des détails du namespace KV: ${namespace}`);
    
    const response = await get(
      `${KV_CACHE_CONFIG.baseRoute}${KV_CACHE_CONFIG.endpoints.namespace}/${namespace}`
    );
    
    if (!response.success) {
      throw new Error(response.error || 'Erreur lors de la récupération du namespace');
    }
    
    debug.log(config, `Détails du namespace ${namespace} récupérés:`, response.data);
    return response.data;
    
  } catch (error) {
    debug.error(config, `Erreur lors de la récupération du namespace ${namespace}:`, error);
    throw error;
  }
}

/**
 * Nettoie les caches KV
 * @param {Object} options - Options de nettoyage
 * @param {string} [options.namespace] - Namespace spécifique à nettoyer
 * @param {string} [options.keyPattern] - Pattern des clés à nettoyer
 * @returns {Promise<Object>} Résultat du nettoyage
 */
export async function clearKVCache(options = {}) {
  try {
    debug.log(config, 'Nettoyage des caches KV...', options);
    
    const response = await post(
      KV_CACHE_CONFIG.baseRoute + KV_CACHE_CONFIG.endpoints.clear,
      options
    );
    
    if (!response.success) {
      throw new Error(response.error || 'Erreur lors du nettoyage des caches KV');
    }
    
    debug.log(config, 'Nettoyage des caches KV terminé:', response.data);
    return response.data;
    
  } catch (error) {
    debug.error(config, 'Erreur lors du nettoyage des caches KV:', error);
    throw error;
  }
}

/**
 * Formate la taille en octets de manière lisible
 * @param {number} bytes - Taille en octets
 * @returns {string} Taille formatée
 */
export function formatBytes(bytes) {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Formate une date de manière relative (il y a X temps)
 * @param {string|Date} date - Date à formater
 * @returns {string} Date formatée
 */
export function formatRelativeTime(date) {
  if (!date) return 'Inconnu';
  
  const now = new Date();
  const targetDate = new Date(date);
  const diffMs = now - targetDate;
  
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSeconds < 60) {
    return `Il y a ${diffSeconds} seconde${diffSeconds > 1 ? 's' : ''}`;
  } else if (diffMinutes < 60) {
    return `Il y a ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  } else if (diffHours < 24) {
    return `Il y a ${diffHours} heure${diffHours > 1 ? 's' : ''}`;
  } else if (diffDays < 30) {
    return `Il y a ${diffDays} jour${diffDays > 1 ? 's' : ''}`;
  } else {
    return targetDate.toLocaleDateString();
  }
}

/**
 * Calcule le pourcentage d'utilisation du cache
 * @param {number} validKeys - Nombre de clés valides
 * @param {number} totalKeys - Nombre total de clés
 * @returns {number} Pourcentage (0-100)
 */
export function calculateCacheEfficiency(validKeys, totalKeys) {
  if (totalKeys === 0) return 0;
  return Math.round((validKeys / totalKeys) * 100);
}

/**
 * Analyse les patterns des clés pour identifier les catégories
 * @param {Array} keys - Liste des clés
 * @returns {Object} Analyse des patterns
 */
export function analyzeKeyPatterns(keys) {
  const patterns = {};
  const prefixes = {};
  
  keys.forEach(keyInfo => {
    const key = keyInfo.key || keyInfo;
    
    // Analyser les préfixes
    const parts = key.split(':');
    if (parts.length > 1) {
      const prefix = parts[0];
      if (!prefixes[prefix]) {
        prefixes[prefix] = {
          count: 0,
          totalSize: 0,
          keys: []
        };
      }
      prefixes[prefix].count++;
      prefixes[prefix].totalSize += keyInfo.size || 0;
      prefixes[prefix].keys.push(key);
    }
    
    // Analyser les patterns communs
    const pattern = key.replace(/[0-9]+/g, '{id}').replace(/[a-f0-9]{8,}/g, '{hash}');
    if (!patterns[pattern]) {
      patterns[pattern] = {
        count: 0,
        examples: []
      };
    }
    patterns[pattern].count++;
    if (patterns[pattern].examples.length < 3) {
      patterns[pattern].examples.push(key);
    }
  });
  
  return { patterns, prefixes };
}

/**
 * Gestionnaire d'événements pour les mises à jour des caches KV
 * Mode manuel uniquement (plus d'appels périodiques)
 */
export class KVCacheMonitor {
  constructor(updateInterval = 10000) {
    this.updateInterval = updateInterval; // Stocké mais non utilisé
    this.intervalId = null;
    this.listeners = new Set();
    this.lastData = null;
  }
  
  /**
   * Démarre la surveillance (mode manuel uniquement - pas d'intervalle automatique)
   */
  start() {
    if (this.intervalId) {
      this.stop();
    }
    
    debug.log(config, 'Initialisation du moniteur de cache KV (mode manuel)');
    
    // Récupération initiale
    this.update();
  }
  
  /**
   * Arrête la surveillance
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      debug.log(config, 'Arrêt de la surveillance des caches KV');
    }
  }
  
  /**
   * Force une mise à jour immédiate
   */
  async update() {
    try {
      const data = await getKVCacheInfo();
      this.lastData = data;
      this.notifyListeners(data);
      return data;
    } catch (error) {
      debug.error(config, 'Erreur lors de la mise à jour des caches KV:', error);
      this.notifyListeners(null, error);
      throw error;
    }
  }
  
  /**
   * Ajoute un listener pour les mises à jour
   * @param {Function} listener - Fonction appelée lors des mises à jour
   */
  addListener(listener) {
    this.listeners.add(listener);
  }
  
  /**
   * Supprime un listener
   * @param {Function} listener - Fonction à supprimer
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }
  
  /**
   * Notifie tous les listeners
   * @param {Object} data - Données à envoyer
   * @param {Error} error - Erreur éventuelle
   */
  notifyListeners(data, error = null) {
    this.listeners.forEach(listener => {
      try {
        listener(data, error);
      } catch (listenerError) {
        debug.error(config, 'Erreur dans un listener de KVCacheMonitor:', listenerError);
      }
    });
  }
  
  /**
   * Récupère les dernières données sans déclencher de requête
   * @returns {Object|null} Dernières données ou null
   */
  getLastData() {
    return this.lastData;
  }
}

// Instance globale du moniteur
export const kvCacheMonitor = new KVCacheMonitor();

// Utilitaires pour l'interface utilisateur
export const KVCacheUIHelpers = {
  /**
   * Génère une couleur basée sur le nom du namespace
   * @param {string} namespace - Nom du namespace
   * @returns {string} Couleur CSS
   */
  getNamespaceColor(namespace) {
    const colors = [
      'bg-blue-100 text-blue-800',
      'bg-green-100 text-green-800', 
      'bg-purple-100 text-purple-800',
      'bg-orange-100 text-orange-800',
      'bg-pink-100 text-pink-800',
      'bg-indigo-100 text-indigo-800'
    ];
    
    let hash = 0;
    for (let i = 0; i < namespace.length; i++) {
      hash = namespace.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  },
  
  /**
   * Détermine l'icône appropriée pour un namespace
   * @param {string} namespace - Nom du namespace
   * @returns {string} Nom de l'icône FontAwesome
   */
  getNamespaceIcon(namespace) {
    const iconMap = {
      'translations': 'faLanguage',
      'languages': 'faGlobe',
      'models': 'faRobot',
      'config': 'faCog',
      'cache': 'faDatabase',
      'api': 'faServer',
      'user': 'faUser'
    };
    
    for (const [key, icon] of Object.entries(iconMap)) {
      if (namespace.toLowerCase().includes(key)) {
        return icon;
      }
    }
    
    return 'faFolder';
  }
};
